import { Flex } from '@fastnorth/kraken/components/layout';
import { forwardRef, type JSX, useLayoutEffect, useMemo, useRef } from 'react';
import { useParticipantDetails } from '../../../../hooks/ParticipantDetailsProvider.tsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@fastnorth/kraken/components/button';
import { Download } from 'lucide-react';
import ShareButton from '../../../../../../../../layout/components/ShareButton.tsx';
import type { FinisherCertificate } from '@fastnorth/timer-api';
import useMergeTagSubstitutions from '../../hooks/useMergeTagSubstitutions.ts';
import normalizeImageUrls from '../../../../../../../../utils/normalizeImageUrls.ts';
import { Text } from '@fastnorth/kraken/components/typography';
import useAppLocation from '../../../../../../../../hooks/useAppLocation.ts';
import { useSearchParams } from 'react-router-dom';
import type { snapdom } from '@zumer/snapdom';
import { LEGACY_CERTIFICATE_HEIGHT, LEGACY_CERTIFICATE_WIDTH } from '../../consts.ts';
import CertificateScalingWrapper from '../certificate-scaling-wrapper/CertificateScalingWrapper.tsx';
import LegacyCertificateContent from './components/legacy-certificate-content/LegacyCertificateContent.tsx';
import useCertificateDownload from '../../hooks/useCertificateDownload.ts';
import type { TemplateSponsorLogo } from '../../../../../../../../graphql/rest-link/types.ts';

type PreviewSponsorLogo = {
  url: string;
  width: number;
  height: number;
};

// The dashboard sends the unsaved logos as a JSON array of cropped image URLs. Anything that is
// not a JSON array fails inside the try, so a malformed value only drops the logos.
const asPreviewSponsorLogos = (value: string): TemplateSponsorLogo[] => {
  try {
    return (JSON.parse(value) as PreviewSponsorLogo[]).map(({ url, width, height }) => ({
      id: url,
      imagePath: url,
      imageWidth: width,
      imageHeight: height,
    }));
  } catch {
    return [];
  }
};

const LegacyCertificate = forwardRef<HTMLDivElement, Record<string, unknown>>(
  (_, ref): JSX.Element | null => {
    const { t } = useTranslation('translation', {
      keyPrefix: 'karwa.components.participant_details.finisher_certificate',
    });
    const [{ resultDetail }] = useParticipantDetails();
    const downloadRef = useRef<HTMLDivElement | null>(null);
    const { isCertificatePreview } = useAppLocation();
    const [searchParams] = useSearchParams();

    const overrides = useMemo(() => {
      if (!isCertificatePreview) {
        return {} as FinisherCertificate;
      }
      return Array.from(searchParams.entries()).reduce(
        (memo, [key, value]) => {
          if (/^(is|has|show).*/.test(key)) {
            memo[key] = value === 'true';
          } else if (/^sponsorLogoMaxHeight/.test(key)) {
            memo.sponsorLogoHeight = parseInt(value, 10) || 0;
          } else if (key === 'sponsorLogos') {
            memo.sponsorLogos = asPreviewSponsorLogos(value);
          } else {
            memo[key] = value;
          }
          return memo;
        },
        {} as Record<string, unknown>
      );
    }, [searchParams, isCertificatePreview]);

    const cert = useMemo(
      () => ({ ...(resultDetail?.finisherCertificate ?? {}), ...overrides }) as FinisherCertificate,
      [overrides, resultDetail?.finisherCertificate]
    );

    const { content } = cert;

    const derivedContent = useMergeTagSubstitutions({ content: content || '' });
    const substitutedContent = useMemo(() => normalizeImageUrls(derivedContent), [derivedContent]);
    const filename = resultDetail?.finisherCertificateFilename ?? 'finisher-certificate.png';
    const captureOptions = useMemo(
      () =>
        ({
          cache: 'full',
          dpr: 1,
        }) as Parameters<typeof snapdom>[1],
      []
    );
    const readinessKey = useMemo(() => ({ cert, substitutedContent }), [cert, substitutedContent]);
    const { downloadCertificate, isDownloading, isReady } = useCertificateDownload({
      captureOptions,
      captureRef: downloadRef,
      filename,
      readinessKey,
    });

    useLayoutEffect(() => {
      if (downloadRef.current && isCertificatePreview) {
        const { width, height } = downloadRef.current.getBoundingClientRect();
        const dimensions = { width, height };
        window.parent.postMessage({ type: 'certificate-dimensions', dimensions }, '*');
      }
    }, [isCertificatePreview, resultDetail]);

    if (!resultDetail?.finisherCertificate) {
      return null;
    }

    return (
      <Flex ref={ref} className={'flex-col w-full gap-4'}>
        <Flex
          ref={downloadRef}
          data-testid={'download-certificate'}
          aria-hidden
          inert
          className={'fixed left-[-9999px] top-0 flex-col'}>
          <LegacyCertificateContent certificate={cert} substitutedContent={substitutedContent} />
        </Flex>
        <Flex className={'w-full drop-shadow-[2px_2px_6px_rgba(0,0,0,0.1)]'}>
          <CertificateScalingWrapper
            height={LEGACY_CERTIFICATE_HEIGHT}
            width={LEGACY_CERTIFICATE_WIDTH}>
            <LegacyCertificateContent
              certificate={cert}
              substitutedContent={substitutedContent}
              testId={'certificate-container'}
            />
          </CertificateScalingWrapper>
        </Flex>
        {!isCertificatePreview && (
          <Flex gap={'5'} className="lg:ml-6 lg:self-end">
            <Button
              disabled={!isReady || isDownloading}
              size="default"
              variant="outline"
              onClick={() => void downloadCertificate()}>
              <>
                <Download width={16} height={16} className={'mr-3'} />
                <Text>{t('download_text')}</Text>
              </>
            </Button>
            <ShareButton trackingLocation={'finisher_cert'} />
          </Flex>
        )}
      </Flex>
    );
  }
);

LegacyCertificate.displayName = 'LegacyCertificate';

export default LegacyCertificate;
